// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { Play, Pause, ChevronLeft, ChevronRight, Menu, ArrowLeft, BookOpen, Book } from 'lucide-react';
import Connections from './components/Connections';
import HTMLWithAudioRenderer from './components/HTMLWithAudioRenderer';
import NavigationMenu from './components/NavigationMenu';
import LandingPage from './components/LandingPage';
import CertificateModal from './components/CertificateModal';
import AboutPage from './components/AboutPage';
import HelpPage from './components/HelpPage';
import AppMenu from './components/AppMenu';
import Dictionary from './components/Dictionary';
import AuthService from './services/AuthService';
import ProgressService from './services/ProgressService';

const API_BASE_URL = '/api'; // Replace with your actual API domain
const ASSETS_URL = ''

// AuthModal Component - Updated to use real authentication
// Updated AuthModal Component with password confirmation
const AuthModal = ({ isOpen, onClose, onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '', name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Check password confirmation for registration
    if (!isLogin && formData.password !== formData.confirmPassword) {
      setError('პაროლები არ ემთხვევა');
      setLoading(false);
      return;
    }

    try {
      let result;
      if (isLogin) {
        result = await AuthService.login(formData.email, formData.password);
      } else {
        result = await AuthService.register(formData.name, formData.email, formData.password);
      }

      if (result.success) {
        onLogin(result.user);
        onClose();
        // Sync any queued progress after login
        await ProgressService.syncQueuedProgress();
      } else {
        setError(result.message || 'Authentication failed');
      }
    } catch (error) {
      console.error('Auth error:', error);
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', confirmPassword: '', name: '' });
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 relative">
        <h2 className="text-xl font-bold mb-4 font-['bpg-caps']">
          {isLogin ? 'შესვლა' : 'რეგისტრაცია'}
        </h2>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <input
              type="text"
              placeholder="სახელი"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg"
              required={!isLogin}
              disabled={loading}
            />
          )}
          
          <input
            type="email"
            placeholder="ელ. ფოსტე"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="w-full p-3 border border-gray-300 rounded-lg"
            required
            disabled={loading}
          />
          
          <input
            type="password"
            placeholder="პაროლი"
            value={formData.password}
            onChange={(e) => setFormData({...formData, password: e.target.value})}
            className="w-full p-3 border border-gray-300 rounded-lg"
            required
            disabled={loading}
            minLength={6}
          />

          {!isLogin && (
            <input
              type="password"
              placeholder="პაროლის დადასტურება"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              className={`w-full p-3 border rounded-lg ${
                formData.confirmPassword && formData.password !== formData.confirmPassword 
                  ? 'border-red-300 bg-red-50' 
                  : 'border-gray-300'
              }`}
              required={!isLogin}
              disabled={loading}
              minLength={6}
            />
          )}
          
          {!isLogin && formData.confirmPassword && formData.password !== formData.confirmPassword && (
            <div className="text-red-600 text-sm">
              პაროლები არ ემთხვევა
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading || (!isLogin && formData.password !== formData.confirmPassword)}
            className="w-full bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 transition-colors font-['bpg-caps'] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'მუშაობა...' : (isLogin ? 'შესვლა' : 'რეგისტრაცია')}
          </button>
        </form>
        
        <div className="mt-4 text-center">
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              resetForm();
            }}
            className="text-blue-600 hover:underline text-sm"
            disabled={loading}
          >
            {isLogin ? 'არ გაქვთ ანგარიში? რეგისტრაცია' : 'უკვე გაქვთ ანგარიში? შესვლა'}
          </button>
        </div>
        
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          disabled={loading}
        >
          ×
        </button>
      </div>
    </div>
  );
};

// Main App component with Router
const App = () => {
  return (
    <Router basename="/">
      <AppContent />
    </Router>
  );
};

// App content component
const AppContent = () => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [showAppMenu, setShowAppMenu] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (AuthService.isAuthenticated()) {
          const result = await AuthService.getCurrentUser();
          if (result.success) {
            setUser(result.user);
            // Load user progress after authentication
            await ProgressService.getUserProgress();
          } else {
            // Token expired or invalid
            await AuthService.logout();
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const handleLogin = async (userData) => {
    setUser(userData);
    // Load progress after login
    await ProgressService.getUserProgress();
  };

  const handleLogout = async () => {
    await AuthService.logout();
    ProgressService.clearCache();
    setUser(null);
  };

  const isLessonRoute = location.pathname.startsWith('/card/');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">იტვირთება...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* App Menu */}
      <AppMenu 
        isOpen={showAppMenu}
        onClose={() => setShowAppMenu(false)}
        user={user}
        onOpenAuth={() => setShowAuth(true)}
        onLogout={handleLogout}
      />

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onLogin={handleLogin}
      />

      <Routes>
        <Route 
          path="/" 
          element={
            <LandingPage 
              onOpenAppMenu={() => setShowAppMenu(true)}
              user={user}
              onOpenAuth={() => setShowAuth(true)}
            />
          } 
        />
        <Route path="/menu" element={<CourseDashboard onOpenAppMenu={() => setShowAppMenu(true)} user={user} />} />
        <Route path="/about" element={<AboutPage onOpenAppMenu={() => setShowAppMenu(true)} />} />
        <Route path="/help" element={<HelpPage onOpenAppMenu={() => setShowAppMenu(true)} />} />
        <Route path="/card/:cardId" element={<LessonApp onOpenAppMenu={() => setShowAppMenu(true)} user={user} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

// Enhanced Course Dashboard Component with dual progress system
const CourseDashboard = ({ onOpenAppMenu, user }) => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [navigationData, setNavigationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progressMap, setProgressMap] = useState({});
  const [showResetModal, setShowResetModal] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch both lessons and navigation data
        const [lessonsResponse, navigationResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/cards/lessons`),
          fetch(`${API_BASE_URL}/cards/navigation`)
        ]);

        if (lessonsResponse.ok) {
          const lessonsData = await lessonsResponse.json();
          setLessons(lessonsData);
        }

        if (navigationResponse.ok) {
          const navigationData = await navigationResponse.json();
          setNavigationData(navigationData);
        }

        // Load user progress if authenticated
        if (user) {
          await ProgressService.getUserProgress();
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Load progress data
  useEffect(() => {
    const loadProgress = () => {
      if (!lessons.length || !navigationData.length || !user) {
        return;
      }
  
      const progress = {};
      for (const lesson of lessons) {
        // Get both sublesson and exercise progress (now synchronous)
        const subLessonProg = getSubLessonProgress(lesson.lessonId);
        const exerciseProg = getExerciseProgress(lesson.lessonId);
        
        progress[lesson.lessonId] = {
          sublesson: subLessonProg,
          exercise: exerciseProg
        };
      }
      setProgressMap(progress);
    };
    
    loadProgress(); // Now synchronous, no await needed
  }, [lessons, navigationData, user]);

  // Show certificate when all lessons are completed
  useEffect(() => {
    if (!user || !lessons.length || !Object.keys(progressMap).length) return;
    let completedLessons = 0;
    lessons.forEach(lesson => {
      const progress = progressMap[lesson.lessonId];
      if (progress && progress.sublesson && progress.sublesson.status === 'completed') {
        completedLessons++;
      }
    });
    if (completedLessons === lessons.length) {
      setShowCertificate(true);
    }
  }, [progressMap]);

  // Helper function to check sublesson completion
  const getSubLessonCompletion = (subLesson) => {
    if (!subLesson.children || subLesson.children.length === 0) {
      return { isCompleted: false, percentage: 0 };
    }
  
    let totalExercises = 0;
    let completedExercises = 0;
  
    for (const exercise of subLesson.children) {
      if (exercise.type === 'exercise' && exercise.cardType === 'exercise') {
        totalExercises++;
        if (ProgressService.isExerciseCompleted(exercise.id)) {
          completedExercises++;
        }
      }
    }
  
    const percentage = totalExercises > 0 ? Math.round((completedExercises / totalExercises) * 100) : 0;
    return {
      isCompleted: percentage === 100,
      percentage
    };
  };

  // Function to get SUBLESSON progress (for main display like "1/4")
  const getSubLessonProgress = (lessonId) => {
    if (!user) {
      return { percentage: 0, completed: 0, total: 0, status: 'remaining' };
    }
  
    const lesson = navigationData.find(item => item.id === lessonId);
    if (!lesson || !lesson.children) {
      return { percentage: 0, completed: 0, total: 0, status: 'remaining' };
    }
  
    let totalSubLessons = 0;
    let completedSubLessons = 0;
  
    // Count sublessons and check their completion
    for (const child of lesson.children) {
      if (child.type === 'subLesson') {
        totalSubLessons++;
        
        // Check if this sublesson is completed (all its exercises done)
        const subLessonCompletion = getSubLessonCompletion(child);
        if (subLessonCompletion.isCompleted) {
          completedSubLessons++;
        }
      }
    }
  
    const percentage = totalSubLessons > 0 ? Math.round((completedSubLessons / totalSubLessons) * 100) : 0;
    
    let status = 'remaining';
    if (percentage === 100) {
      status = 'completed';
    } else if (percentage > 0) {
      status = 'in-progress';
    }
  
    return {
      percentage,
      completed: completedSubLessons,
      total: totalSubLessons,
      status
    };
  };

  // Function to get EXERCISE progress (for progress bar detail like "1/5 ვარჯიში")
  const getExerciseProgress = (lessonId) => {
    if (!user) {
      return { percentage: 0, completed: 0, total: 0 };
    }
  
    const lesson = navigationData.find(item => item.id === lessonId);
    if (!lesson) {
      return { percentage: 0, completed: 0, total: 0 };
    }
  
    let totalExercises = 0;
    let completedExercises = 0;
  
    // Recursive function to count exercises (now synchronous)
    const countExercises = (items) => {
      for (const item of items) {
        if (item.type === 'exercise' && item.cardType === 'exercise') {
          totalExercises++;
          if (ProgressService.isExerciseCompleted(item.id)) {
            completedExercises++;
          }
        } else if (item.children && item.children.length > 0) {
          countExercises(item.children);
        }
      }
    };
  
    if (lesson.children) {
      countExercises(lesson.children);
    }
  
    const percentage = totalExercises > 0 ? Math.round((completedExercises / totalExercises) * 100) : 0;
  
    return {
      percentage,
      completed: completedExercises,
      total: totalExercises
    };
  };

  // Get overall progress statistics
  const getOverallProgress = () => {
    if (!user || !navigationData.length) {
      return { completed: 0, remaining: lessons.length, total: lessons.length };
    }

    let completedLessons = 0;
    lessons.forEach(lesson => {
      const progress = progressMap[lesson.lessonId];
      if (progress && progress.sublesson && progress.sublesson.status === 'completed') {
        completedLessons++;
      }
    });

    return {
      completed: completedLessons,
      remaining: lessons.length - completedLessons,
      total: lessons.length
    };
  };

  // Get the first card ID of a lesson
  const getFirstCardOfLesson = (lessonId) => {
    const lesson = navigationData.find(item => item.id === lessonId);
    if (lesson && lesson.children && lesson.children.length > 0) {
      return lesson.children[0].id;
    }
    return 7; // fallback
  };

  // Find next incomplete sub-lesson across all lessons
  const getNextIncompleteSubLesson = () => {
    const findFirstIncomplete = (items) => {
      for (const item of items) {
        if (item.type === 'subLesson') {
          // Check if this sublesson is completed using the same logic as progress calculation
          const subLessonCompletion = getSubLessonCompletion(item);
          
          // If this sublesson is NOT completed, return it
          if (!subLessonCompletion.isCompleted) {
            return item.id;
          }
        } else if (item.children && item.children.length > 0) {
          const result = findFirstIncomplete(item.children);
          if (result) return result;
        }
      }
      return null;
    };
  
    return findFirstIncomplete(navigationData);
  };

  const handleStartCourse = () => {
    const nextIncompleteSubLesson = getNextIncompleteSubLesson();
    
    if (nextIncompleteSubLesson) {
      navigate(`/card/${nextIncompleteSubLesson}`);
    } else {
      // If no incomplete exercises, go to first lesson
      const firstLessonCard = getFirstCardOfLesson(lessons[0]?.lessonId);
      navigate(`/card/${firstLessonCard}`);
    }
  };

  const handleLessonClick = async (lesson) => {
    const firstCardId = getFirstCardOfLesson(lesson.lessonId);
    
    // Start the lesson if authenticated
    if (user) {
      try {
        await ProgressService.startLesson(lesson.lessonId);
      } catch (error) {
        console.error('Failed to start lesson:', error);
        // Continue navigation even if tracking fails
      }
    }
    
    navigate(`/card/${firstCardId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'in-progress':
        return 'text-blue-600';
      default:
        return 'text-red-600';
    }
  };

  const getStatusText = (progress) => {
    if (!progress || !progress.sublesson) return 'დარჩენილი (0/0)';
    
    const subLessonProgress = progress.sublesson;
    
    if (subLessonProgress.status === 'completed') {
      return 'დასრულებული';
    } else if (subLessonProgress.status === 'in-progress') {
      return `${subLessonProgress.percentage}% (${subLessonProgress.completed}/${subLessonProgress.total})`;
    } else {
      return `დარჩენილი (0/${subLessonProgress.total})`;
    }
  };

  const getProgressBarColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'in-progress':
        return 'bg-blue-500';
      default:
        return 'bg-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-['HelveticaRegular']">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={onOpenAppMenu}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Menu size={20} />
              </button>
              <div className="flex items-center gap-2">
  <BookOpen className="text-blue-600" size={24} />
  <h1 className="text-xl font-bold text-gray-900 font-['bpg-caps']">
    აფხაზური ენის კურსი
  </h1>
</div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft size={16} />
              <span className="font-['bpg-caps']">მთავარი</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
  <h2 className="text-2xl font-bold font-['bpg-caps']">კურსის შინაარსი</h2>
  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
    {user && Object.keys(progressMap).some(lessonId => 
      progressMap[lessonId]?.exercise?.completed > 0 || 
      progressMap[lessonId]?.sublesson?.completed > 0
    ) && (
      <button
        onClick={() => setShowResetModal(true)}
        className="flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-semibold font-['bpg-caps']"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="1 4 1 10 7 10"></polyline>
          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
        </svg>
        პროგრესის განულება
      </button>
    )}
    <button
      onClick={handleStartCourse}
      className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold font-['bpg-caps']"
    >
      <Play size={18} />
      კურსის დაწყება
    </button>
  </div>
</div>
          
          {!user && (
  <div className="mb-6 p-4 bg-blue-50 text-blue-800 rounded-lg">
    <p className="text-sm">
      შედით სისტემაში, რომ თქვენი პროგრესი შეინახოს და სინქრონიზდეს ყველა მოწყობილობაზე
    </p>
  </div>
)}

 
          {loading ? (
            <div className="text-center py-8">
              <div className="text-gray-500">იტვირთება...</div>
            </div>
          ) : (
            <div className="space-y-4">
              {lessons.map((lesson, index) => {
                const progress = progressMap[lesson.lessonId];
                const subLessonProgress = progress?.sublesson || { percentage: 0, completed: 0, total: 0, status: 'remaining' };
                const exerciseProgress = progress?.exercise || { percentage: 0, completed: 0, total: 0 };
                
                return (
                  <div
                    key={lesson.lessonId}
                    className="group rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all duration-200"
                    onClick={() => handleLessonClick(lesson)}
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <BookOpen size={16} className="text-blue-600 flex-shrink-0" />
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-800 font-['bpg-caps']">
                              გაკვეთილი N{index + 1}
                            </h4>
                            {lesson.description && (
                              <p className="text-sm text-gray-600 mt-1">
                                {lesson.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm font-medium ${getStatusColor(subLessonProgress.status)}`}>
                            {getStatusText(progress)}
                          </div>
                        </div>
                      </div>
                      
                      {/* Progress Bar - shows exercise progress */}
                      {user && exerciseProgress.total > 0 && (
                        <div className="mt-3">
                          <div className="flex justify-between items-center text-xs text-gray-500 mb-1">
                            <span>პროგრესი</span>
                            <span>{exerciseProgress.completed}/{exerciseProgress.total} სავარჯიშო</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor(subLessonProgress.status)}`}
                              style={{ width: `${exerciseProgress.percentage}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>

{/* Reset Progress Confirmation Modal */}
{showResetModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
          <span className="text-red-600 text-xl">⚠</span>
        </div>
        <h3 className="text-lg font-bold font-['bpg-caps']">პროგრესის განულება</h3>
      </div>
      
      <div className="mb-6">
        <p className="text-gray-700 mb-3">
          დარწმუნებული ხართ, რომ გსურთ მთელი პროგრესის განულება?
        </p>
        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
          <p>მიმდინარე პროგრესი:</p>
          <p>• დასრულებული გაკვეთილები: {getOverallProgress().completed}</p>
          <p>• ყველა მიღწევა და სტატისტიკა</p>
        </div>
        <p className="text-red-600 font-medium mt-2 text-sm">
          ეს მოქმედება შეუქცევადია!
        </p>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => setShowResetModal(false)}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-['bpg-caps']"
        >
          გაუქმება
        </button>
        <button
          onClick={async () => {
            try {
              const result = await ProgressService.resetProgress();
              if (result.success) {
                // Clear local caches and refresh
                ProgressService.clearCache();
                window.location.reload();
              } else {
                alert('შეცდომა პროგრესის განულებისას');
              }
            } catch (error) {
              console.error('Reset error:', error);
              alert('შეცდომა მოხდა');
            }
            setShowResetModal(false);
          }}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-['bpg-caps']"
        >
          დიახ, გაანულე
        </button>
      </div>
    </div>
  </div>
)}
<CertificateModal
  isOpen={showCertificate}
  onClose={() => setShowCertificate(false)}
/>
</div>
);
};

// Main lesson app component
const LessonApp = ({ onOpenAppMenu, user }) => {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [cards, setCards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completedExercises, setCompletedExercises] = useState(new Set());
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);
  const [showMiniPlayer, setShowMiniPlayer] = useState(false);
  const [showDictionary, setShowDictionary] = useState(false);
  const audioPlayerRef = useRef(null);
  console.log('LessonApp render - currentCardIndex:', currentCardIndex, 'showKeyboard:', showKeyboard);
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('app_loaded');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    const isPageRefresh = !sessionStorage.getItem('app_loaded');
    if (isPageRefresh) {
      const storageKeys = Object.keys(localStorage);
      storageKeys.forEach(key => {
        if (key.startsWith('textInputs_') || key.startsWith('connections_') || key.startsWith('shuffled_')) {
          localStorage.removeItem(key);
        }
      });
    }
    sessionStorage.setItem('app_loaded', 'true');

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const currentCard = cards[currentCardIndex];
    const shouldShowMiniPlayer = currentCard?.cardType === 'exercise' && currentCard?.audioUrl;
    setShowMiniPlayer(shouldShowMiniPlayer);
  }, [cards, currentCardIndex]);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/cards`);
        if (!response.ok) {
          throw new Error('Failed to fetch cards');
        }
        const data = await response.json();
        const validCards = data.map(card => {
          
          if (card.selectionOptions) {
            card.selectionOptions = card.selectionOptions.filter(
              option => option.optionText !== null && option.optionId !== 0
            );
          }
          
          if (card.textInputs) {
            card.textInputs = card.textInputs.filter(
              input => input.correctAnswer !== null && input.inputId !== 0
            );
          }
          
          if (card.connections) {
            card.connections = card.connections.filter(
              conn => conn.fromText !== null && conn.toText !== null && conn.pairId !== 0
            );
          }
          
          return card;
        });

        setCards(validCards);
        
        if (cardId) {
          const cardIndex = validCards.findIndex(card => card.cardId === parseInt(cardId));
          if (cardIndex !== -1) {
            setCurrentCardIndex(cardIndex);
          }
        }

        // Load user progress and set completed exercises
        if (user) {
          const progressResult = await ProgressService.getUserProgress();
          if (progressResult.success) {
            const completed = new Set();
            validCards.forEach(card => {
              if (card.cardType === 'exercise' && ProgressService.isExerciseCompleted(card.cardId)) {
                const cardIndex = validCards.findIndex(c => c.cardId === card.cardId);
                if (cardIndex !== -1) {
                  completed.add(cardIndex);
                }
              }
            });
            setCompletedExercises(completed);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('API Error:', err);
        setError('Failed to load cards. Please try again later.');
        setLoading(false);
      }
    };

    fetchCards();
  }, [cardId, user]);

  useEffect(() => {
    console.log('Navigation effect triggered', { cardsLength: cards.length, currentCardIndex });
    if (cards.length > 0 && cards[currentCardIndex]) {
      const currentCard = cards[currentCardIndex];
      const currentUrl = location.pathname;
      const targetUrl = `/card/${currentCard.cardId}`;
      
      // Only navigate if we're not already on the correct URL
      if (currentUrl !== targetUrl) {
        navigate(`/card/${currentCard.cardId}`, { replace: true });
      }
    }
  }, [currentCardIndex, navigate, location.pathname]);


  const isCurrentExerciseCompleted = (cardIndex) => {
    return completedExercises.has(cardIndex);
  };

  const handleExerciseComplete = async (cardIndex) => {
    const card = cards[cardIndex];
    if (!card || card.cardType !== 'exercise') return;

    setCompletedExercises(prev => new Set([...prev, cardIndex]));
    
    // Save progress to backend if user is authenticated
    if (user) {
      try {
        await ProgressService.completeExercise(card.cardId, {
          cardId: card.cardId,
          exerciseType: card.exerciseType,
          completedAt: new Date().toISOString()
        });
      } catch (error) {
        console.error('Failed to save exercise progress:', error);
        // Exercise completion is still tracked locally even if backend fails
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">იტვირთება...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen text-red-500">
        {error}
      </div>
    );
  }

  const currentCard = cards[currentCardIndex];

  const handleNext = () => {
    if (currentCardIndex < cards.length - 1) {
      setCurrentCardIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(prev => prev - 1);
    }
  };

  const handleNavigationToggle = () => {
    setShowNavigation(!showNavigation);
  };

  const handleNavigateToCard = (cardId) => {
    const cardIndex = cards.findIndex(card => card.cardId === cardId);
    if (cardIndex !== -1) {
      setCurrentCardIndex(cardIndex);
    }
    setShowNavigation(false);
  };

  const hasValidExerciseData = (card) => {
    if (card.cardType !== 'exercise') return true;
    
    switch (card.exerciseType) {
      case 'selection':
        return card.selectionOptions && card.selectionOptions.length > 0;
      case 'text_input':
        return card.textInputs && card.textInputs.length > 0;
      case 'connection':
        return card.connections && card.connections.length > 0;
      default:
        return false;
    }
  };


  // MiniAudioPlayer Component for bottom fixed player
  const MiniAudioPlayer = ({ url, title }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const audioRef = useRef(new Audio(url));

    const togglePlay = () => {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    };

    const formatTime = (time) => {
      if (isNaN(time)) return '0:00';
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

    useEffect(() => {
      const audio = audioRef.current;
      
      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
        setIsLoading(false);
      };
      
      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
      };
      
      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      const handleLoadStart = () => {
        setIsLoading(true);
      };

      const handleCanPlay = () => {
        setIsLoading(false);
      };

      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('canplay', handleCanPlay);

      audio.load();

      return () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('loadstart', handleLoadStart);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.pause();
      };
    }, [url]);

    return (
      <div className="flex items-center gap-2 w-full">
        <button
          onClick={togglePlay}
          disabled={isLoading}
          className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors disabled:opacity-50 flex-shrink-0"
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : isPlaying ? (
            <Pause size={16} />
          ) : (
            <Play size={16} />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-gray-800 truncate mb-1 font-['bpg-caps']">
            {title}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{formatTime(currentTime)}</span>
            <div 
              className="flex-1 h-3 bg-gray-300 rounded-full cursor-pointer relative group"
              onClick={(e) => {
                if (duration > 0) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const clickX = e.clientX - rect.left;
                  const percentage = clickX / rect.width;
                  const newTime = percentage * duration;
                  audioRef.current.currentTime = newTime;
                  setCurrentTime(newTime);
                }
              }}
              onMouseDown={(e) => {
                if (duration > 0) {
                  const progressBar = e.currentTarget;
                  
                  const handleMouseMove = (moveEvent) => {
                    const rect = progressBar.getBoundingClientRect();
                    const clickX = Math.max(0, Math.min(moveEvent.clientX - rect.left, rect.width));
                    const percentage = clickX / rect.width;
                    const newTime = percentage * duration;
                    audioRef.current.currentTime = newTime;
                    setCurrentTime(newTime);
                  };
                  
                  const handleMouseUp = () => {
                    document.removeEventListener('mousemove', handleMouseMove);
                    document.removeEventListener('mouseup', handleMouseUp);
                  };
                  
                  document.addEventListener('mousemove', handleMouseMove);
                  document.addEventListener('mouseup', handleMouseUp);
                }
              }}
            >
              <div 
                className="h-3 bg-blue-500 rounded-full transition-none"
                style={{ width: `${progressPercentage}%` }}
              />
              {/* Seek knob */}
              <div 
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-blue-600 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `calc(${progressPercentage}% - 8px)` }}
              />
            </div>
            <span className="text-xs text-gray-500">{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    );
  };

  // AudioPlayer Component - keeping existing implementation
  const AudioPlayer = ({ url }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const audioRef = useRef(new Audio(url));

    const togglePlay = () => {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    };

    const handleSeek = (e) => {
      const progressBar = e.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const progressBarWidth = rect.width;
      const seekTime = (clickX / progressBarWidth) * duration;
      
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    };

    const formatTime = (time) => {
      if (isNaN(time)) return '0:00';
      
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

    useEffect(() => {
      const audio = audioRef.current;
      
      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
        setIsLoading(false);
      };
      
      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
      };
      
      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      const handleLoadStart = () => {
        setIsLoading(true);
      };

      const handleCanPlay = () => {
        setIsLoading(false);
      };

      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('canplay', handleCanPlay);

      audio.load();

      return () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('loadstart', handleLoadStart);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.pause();
      };
    }, [url]);

    return (
      <div className="rounded-lg p-4 w-full">
        <div className="flex items-center gap-4">
          <button
            onClick={togglePlay}
            disabled={isLoading}
            className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : isPlaying ? (
              <Pause size={24} />
            ) : (
              <Play size={24} />
            )}
          </button>

          <div className="flex-1 space-y-1">
            <div 
              className="w-full h-2 bg-gray-300 rounded-full cursor-pointer hover:bg-gray-400 transition-colors"
              onClick={handleSeek}
            >
              <div 
                className="h-full bg-blue-500 rounded-full transition-all duration-100"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            
            <div className="flex justify-between text-sm text-gray-600">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // SuccessOverlay component for reuse
  const SuccessOverlay = () => (
    <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-40">
      <div className="bg-green-100 text-green-800 px-4 py-2 rounded-lg shadow-lg">
      Аферым!✨
      </div>
    </div>
  );

  // Updated SelectableItems Component with word-list design
// Updated SelectableItems Component with order validation and feedback
// Updated SelectableItems Component with fixed sequence order detection
const SelectableItems = ({ items, itemsPerRow = 2, onComplete, isCompleted: externalIsCompleted }) => {
  const [selected, setSelected] = useState(new Set());
  const [selectionOrder, setSelectionOrder] = useState([]);
  const [wrongSelections, setWrongSelections] = useState(new Set());
  const [isCompleted, setIsCompleted] = useState(externalIsCompleted);
  
  useEffect(() => {
    if (externalIsCompleted) {
      setIsCompleted(true);
    }
  }, [externalIsCompleted]);

  // Responsive grid classes based on itemsPerRow
  const getResponsiveGridClass = (itemsPerRow) => {
    const baseClasses = 'grid grid-cols-1 sm:grid-cols-2';
    
    if (itemsPerRow <= 2) return baseClasses;
    
    if (itemsPerRow <= 4) {
      return `grid grid-cols-1 sm:grid-cols-2 md:grid-cols-${itemsPerRow}`;
    }
    
    if (itemsPerRow <= 6) {
      return `grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-${itemsPerRow}`;
    }
    
    return `grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-${Math.min(itemsPerRow, 12)}`;
  };

  // Check if all correct items are selected
  const checkAllCorrect = React.useCallback((newSelected) => {
    return items.every(item => 
      (item.isCorrect && newSelected.has(item.optionId)) ||
      (!item.isCorrect && !newSelected.has(item.optionId))
    );
  }, [items]);

  // Check if selection order matches pronunciation order
  const checkSelectionOrder = React.useCallback((orderArray) => {
    // Get only the correct items that should be selected
    const correctItems = items.filter(item => item.isCorrect);
    
    // FIXED: Better check for sequence order
    const hasSequenceOrder = correctItems.some(item => 
      typeof item.sequenceOrder === 'number' && item.sequenceOrder >= 0
    );
    
    console.log('=== ORDER CHECK DEBUG ===');
    console.log('Correct items:', correctItems.map(i => ({ id: i.optionId, text: i.optionText, seq: i.sequenceOrder })));
    console.log('Has sequence order?', hasSequenceOrder);
    
    if (!hasSequenceOrder) {
      console.log('No sequence order defined, skipping order validation');
      return true; // Fall back to original behavior if no order specified
    }
    
    // Sort correct items by their sequence order
    const expectedOrder = correctItems
      .filter(item => typeof item.sequenceOrder === 'number')
      .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
      .map(item => item.optionId);
    
    // Filter selection order to only include correct items
    const selectedCorrectItems = orderArray.filter(optionId => 
      correctItems.some(item => item.optionId === optionId)
    );
    
    console.log('Expected order:', expectedOrder);
    console.log('Selected correct items in order:', selectedCorrectItems);
    console.log('Order matches?', JSON.stringify(selectedCorrectItems) === JSON.stringify(expectedOrder));
    console.log('=== END ORDER CHECK DEBUG ===');
    
    // Check if the selection order matches expected order
    return JSON.stringify(selectedCorrectItems) === JSON.stringify(expectedOrder);
  }, [items]);

  // Get expected next selection based on sequence order
// Get expected next selection based on sequence order
const getExpectedNextSelection = React.useCallback((currentOrder = selectionOrder) => {
  const correctItems = items.filter(item => 
    item.isCorrect && typeof item.sequenceOrder === 'number'
  );
  
  if (correctItems.length === 0) return null;
  
  const sortedCorrectItems = correctItems.sort((a, b) => a.sequenceOrder - b.sequenceOrder);
  
  // FIXED: Count only items selected in correct sequence order from the beginning
  let validSequenceProgress = 0;
  
  // Filter currentOrder to only include correct items first
  const correctSelections = currentOrder.filter(optionId => 
    correctItems.some(item => item.optionId === optionId)
  );
  
  // Now check how many from the beginning of the sequence match the expected order
  for (let i = 0; i < Math.min(correctSelections.length, sortedCorrectItems.length); i++) {
    if (correctSelections[i] === sortedCorrectItems[i].optionId) {
      validSequenceProgress++;
    } else {
      // Stop at first mismatch - no more valid progress
      break;
    }
  }
  
  if (validSequenceProgress >= sortedCorrectItems.length) {
    return null; // Sequence complete
  }
  
  return sortedCorrectItems[validSequenceProgress];
}, [items, selectionOrder]);

  // Handle item selection with order tracking and feedback
  const handleSelect = (itemId) => {
    if (isCompleted) return;
    
    const newSelected = new Set(selected);
    let newOrder = [...selectionOrder];
    let newWrongSelections = new Set(wrongSelections);
    
    console.log('=== HANDLE SELECT DEBUG ===');
    console.log('Selected item ID:', itemId);
    console.log('All items:', items.map(i => ({ 
      id: i.optionId, 
      text: i.optionText, 
      isCorrect: i.isCorrect, 
      sequenceOrder: i.sequenceOrder 
    })));
    
    if (newSelected.has(itemId)) {
      // Deselecting
      newSelected.delete(itemId);
      newOrder = newOrder.filter(id => id !== itemId);
      newWrongSelections.delete(itemId);
      console.log('DESELECTING item');
    } else {
      // Selecting - first auto-deselect any wrong selections
      if (newWrongSelections.size > 0) {
        console.log('Auto-deselecting wrong selections:', Array.from(newWrongSelections));
        newWrongSelections.forEach(wrongId => {
          newSelected.delete(wrongId);
          newOrder = newOrder.filter(id => id !== wrongId);
        });
        newWrongSelections.clear();
      }
      
      // Now proceed with new selection
      newSelected.add(itemId);
      newOrder.push(itemId);
      
      const item = items.find(i => i.optionId === itemId);
      
      // Check if we have sequence order
      const hasSequenceOrder = items.some(item => 
        item.isCorrect && typeof item.sequenceOrder === 'number' && item.sequenceOrder >= 0
      );
      
      console.log('Selected item:', item);
      console.log('Has sequence order?', hasSequenceOrder);
      console.log('Current selection order BEFORE this selection:', selectionOrder);
      
      if (!item.isCorrect) {
        // Always mark incorrect items as wrong
        newWrongSelections.add(itemId);
        console.log('MARKING AS WRONG - incorrect item selected');
      } else if (hasSequenceOrder) {
        // For correct items with sequence order, check if it's the expected next
        const expectedNext = getExpectedNextSelection(selectionOrder);
        console.log('Expected next item:', expectedNext);
        console.log('Selected item matches expected?', expectedNext && expectedNext.optionId === itemId);
        
        if (expectedNext && expectedNext.optionId !== itemId) {
          // Correct item selected in wrong order
          newWrongSelections.add(itemId);
          console.log('MARKING AS WRONG - correct item but wrong order');
        } else {
          // Correct item in correct order
          newWrongSelections.delete(itemId);
          console.log('MARKING AS CORRECT - right item in right order');
        }
      } else {
        // No sequence order, just check if it's correct
        newWrongSelections.delete(itemId);
        console.log('No sequence order - marking correct item as valid');
      }
    }
    
    console.log('New wrong selections:', Array.from(newWrongSelections));
    console.log('=== END DEBUG ===');
    
    setSelected(newSelected);
    setSelectionOrder(newOrder);
    setWrongSelections(newWrongSelections);
    
    // Check completion
    const isAllCorrect = checkAllCorrect(newSelected);
    const isOrderCorrect = checkSelectionOrder(newOrder);
    
    console.log('All correct?', isAllCorrect, 'Order correct?', isOrderCorrect);
    
    if (isAllCorrect && isOrderCorrect && !isCompleted) {
      setIsCompleted(true);
      onComplete();     
      console.log(`${ASSETS_URL}/well-done.mp3`);
      const audio = new Audio(`${ASSETS_URL}/well-done.mp3`);
      audio.play();
    }
  };

  return (
    <div className="relative max-w-full mx-auto bg-slate-50 p-4 rounded-xl border border-slate-200">
      {/* Word-list styled container */}
      <div className={`${getResponsiveGridClass(itemsPerRow)} gap-3`}>
        {items.map(item => {
          const isSelected = selected.has(item.optionId);
          const showAsCorrect = isCompleted && item.isCorrect;
          const showAsSelected = isSelected && isCompleted;
          const isWrongSelection = wrongSelections.has(item.optionId);
          const expectedNext = getExpectedNextSelection();
          // Only highlight expected next in green if there are wrong selections (to guide after error)
          const isExpectedNext = expectedNext && expectedNext.optionId === item.optionId && !isCompleted && wrongSelections.size > 0;
          
          // Show selection order number for selected items
          const selectionIndex = selectionOrder.indexOf(item.optionId);
          const orderNumber = selectionIndex >= 0 ? selectionIndex + 1 : null;
          
          // Determine styling based on state
          let borderClass = 'border-l-blue-400';
          let bgClass = 'bg-white';
          let textColor = 'text-gray-800';
          let shadowColor = '0 2px 8px rgba(0, 0, 0, 0.06)';
          
          if (showAsCorrect || showAsSelected) {
            borderClass = 'border-l-green-500';
            bgClass = 'bg-green-50';
            textColor = 'text-green-700';
            shadowColor = '0 4px 16px rgba(34, 197, 94, 0.2)';
          } else if (isWrongSelection) {
            borderClass = 'border-l-red-500';
            bgClass = 'bg-red-50';
            textColor = 'text-red-700';
            shadowColor = '0 4px 16px rgba(239, 68, 68, 0.2)';
          } else if (isExpectedNext) {
            borderClass = 'border-l-green-500';
            bgClass = 'bg-green-50';
            textColor = 'text-green-700';
            shadowColor = '0 4px 16px rgba(34, 197, 94, 0.2)';
          } else if (isSelected) {
            borderClass = 'border-l-blue-500';
            bgClass = 'bg-blue-50';
            textColor = 'text-blue-700';
            shadowColor = '0 4px 16px rgba(59, 130, 246, 0.2)';
          }
          
          return (
            <div
              key={item.optionId}
              onClick={() => handleSelect(item.optionId)}
              className={`
                ${bgClass} p-4 rounded-lg cursor-pointer transition-all duration-300 group relative
                border-l-4 ${borderClass}
                ${isCompleted ? 'cursor-not-allowed' : 'hover:translate-x-1'}
              `}
              style={{ 
                boxShadow: shadowColor,
              }}
              onMouseEnter={(e) => {
                if (!isCompleted) {
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(96, 165, 250, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isCompleted) {
                  e.currentTarget.style.boxShadow = shadowColor;
                }
              }}
            >
              {/* Selection order indicator */}
              {orderNumber && (
                <div className={`absolute top-2 right-2 w-6 h-6 text-white text-xs rounded-full flex items-center justify-center font-bold z-10 ${
                  isWrongSelection ? 'bg-red-500' : 'bg-blue-500'
                }`}>
                  {orderNumber}
                </div>
              )}
              
              <div className="flex items-center gap-3">
                {item.imageUrl && (
                  <div className="flex-shrink-0">
                    <img 
                      src={`${ASSETS_URL}${item.imageUrl}`} 
                      alt="" 
                      className="w-20 h-20 object-cover rounded"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <span className={`font-medium text-base transition-colors ${textColor}`}>
                    {item.optionText}
                  </span>
                </div>
                
                {/* Selection indicator */}
                <div className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all
                  ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}
                  ${showAsCorrect || showAsSelected ? 'border-green-500 bg-green-500' : ''}
                `}>
                  {(isSelected || showAsCorrect) && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {isCompleted && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-40">
          <div className="bg-green-100 text-green-800 px-4 py-2 rounded-lg shadow-lg">
            Аферым!✨
          </div>
        </div>
      )}
    </div>
  );
};
  // Updated TextInputs Component with word-list design from HTML
// Updated TextInputs Component with validation feedback
const TextInputs = ({ inputs, itemsPerRow = 2, onComplete, isCompleted: externalIsCompleted, inlineInputs = false }) => {
  const [showKeyboard, setShowKeyboard] = useState(false);
  const storageKey = `textInputs_${inputs.map(i => i.inputId).join('_')}`;
  console.log('TextInputs storageKey:', storageKey, 'inputs length:', inputs.length);
  const [values, setValues] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : {};
  });
  const [activeInputId, setActiveInputId] = useState(null);
  const [capsLock, setCapsLock] = useState(false);
  const [isCompleted, setIsCompleted] = useState(externalIsCompleted);
  
  // NEW: Add state to track incorrect inputs that have lost focus
  const [incorrectInputs, setIncorrectInputs] = useState(new Set());

  // Helper function to normalize text for comparison (removes punctuation)
  const normalizeText = (text) => {
    return text.replace(/[.!?]/g, '').trim();
  };
  
  // Add this useEffect right here:
  useEffect(() => {
    if (showKeyboard) {
      // Wait for keyboard to render, then measure it
      setTimeout(() => {
        const keyboard = document.querySelector('.fixed.bottom-0');
        if (keyboard) {
          const keyboardHeight = keyboard.offsetHeight;
          document.body.style.paddingBottom = `${keyboardHeight + 20}px`; // +20px for buffer
        } else {
          // Fallback if keyboard not found
          document.body.style.paddingBottom = '320px';
        }
      }, 10);
    } else {
      document.body.style.paddingBottom = '0px';
    }
    
    return () => {
      document.body.style.paddingBottom = '0px';
    };
  }, [showKeyboard]);
  
  // ADD THIS LINE:
  console.log('TextInputs render - values:', values, 'showKeyboard:', showKeyboard, 'isCompleted:', isCompleted);
  
  useEffect(() => {
    if (externalIsCompleted) {
      setIsCompleted(true);
      // Clear incorrect inputs when completed
      setIncorrectInputs(new Set());
      // Restore values from localStorage if they exist
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        setValues(JSON.parse(stored));
      }
    }
  }, [externalIsCompleted, storageKey]);
  
  // Restore responsive grid classes based on itemsPerRow
  const getResponsiveGridClass = (itemsPerRow) => {
    const baseClasses = 'grid gap-3';
    
    // Responsive grid system matching the original functionality
    if (itemsPerRow === 1) {
      return `${baseClasses} grid-cols-1`;
    } else if (itemsPerRow <= 2) {
      return `${baseClasses} grid-cols-1 sm:grid-cols-2`;
    } else if (itemsPerRow <= 3) {
      return `${baseClasses} grid-cols-1 sm:grid-cols-2 md:grid-cols-3`;
    } else if (itemsPerRow <= 5) {
      return `${baseClasses} grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-${itemsPerRow}`;
    } else {
      return `${baseClasses} grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-${Math.min(itemsPerRow, 12)}`;
    }
  };

 const abkhazLetters = [
      { lower: 'а', upper: 'А' },
      { lower: 'б', upper: 'Б' },
      { lower: 'в', upper: 'В' },
      { lower: 'г', upper: 'Г' },
      { lower: 'гь', upper: 'Гь' },
      { lower: 'гӷ', upper: 'Гӷ' },
      { lower: 'ӷ', upper: 'Ӷ' },
      { lower: 'ӷь', upper: 'Ӷь' },
      { lower: 'ӷә', upper: 'Ӷә' },
      { lower: 'д', upper: 'Д' },
      { lower: 'дә', upper: 'Дә' },
      { lower: 'е', upper: 'Е' },
      { lower: 'ж', upper: 'Ж' },
      { lower: 'жь', upper: 'Жь' },
      { lower: 'жә', upper: 'Жә' },
      { lower: 'з', upper: 'З' },
      { lower: 'ӡ', upper: 'Ӡ' },
      { lower: 'ᴣә', upper: 'ӡә' },
      { lower: 'и', upper: 'И' },
      { lower: 'к', upper: 'К' },
      { lower: 'кь', upper: 'Кь' },
      { lower: 'кә', upper: 'Кә' },
      { lower: 'қ', upper: 'Қ' },
      { lower: 'қь', upper: 'Қь' },
      { lower: 'қә', upper: 'Қә' },
      { lower: 'ҟ', upper: 'Ҟ' },
      { lower: 'ҟь', upper: 'Ҟь' },
      { lower: 'ҟә', upper: 'Ҟә' },
      { lower: 'л', upper: 'Л' },
      { lower: 'м', upper: 'М' },
      { lower: 'н', upper: 'Н' },
      { lower: 'о', upper: 'О' },
      { lower: 'п', upper: 'П' },
      { lower: 'ԥ', upper: 'Ԥ' },
      { lower: 'р', upper: 'Р' },
      { lower: 'с', upper: 'С' },
      { lower: 'т', upper: 'Т' },
      { lower: 'тә', upper: 'Тә' },
      { lower: 'ҭ', upper: 'Ҭ' },
      { lower: 'ҭә', upper: 'Ҭә' },
      { lower: 'у', upper: 'У' },
      { lower: 'ф', upper: 'Ф' },
      { lower: 'х', upper: 'Х' },
      { lower: 'хь', upper: 'Хь' },
      { lower: 'хә', upper: 'Хә' },
      { lower: 'ҳ', upper: 'Ҳ' },
      { lower: 'ҳә', upper: 'Ҳә' },
      { lower: 'ц', upper: 'Ц' },
      { lower: 'цә', upper: 'Цә' },
      { lower: 'ҵ', upper: 'Ҵ' },
      { lower: 'ҵә', upper: 'Ҵә' },
      { lower: 'ч', upper: 'Ч' },
      { lower: 'ҷ', upper: 'Ҷ' },
      { lower: 'ҽ', upper: 'Ҽ' },
      { lower: 'ҿ', upper: 'Ҿ' },
      { lower: 'ш', upper: 'Ш' },
      { lower: 'шь', upper: 'Шь' },
      { lower: 'шә', upper: 'Шә' },
      { lower: 'џ', upper: 'Џ' },
      { lower: 'џь', upper: 'Џь' },
      { lower: 'ҩ', upper: 'Ҩ' },
      { lower: 'ы', upper: 'Ы' },
      { lower: 'ь', upper: 'Ь' },
      { lower: 'ә', upper: 'Ә' },
      { lower: '-', upper: '-' },
      { lower: ' ', upper: ' ', display: 'Space' } 
      ];


  const handleKeyPress = (letter) => {
    if (activeInputId !== null && !isCompleted) {
      const newValues = { ...values };
      newValues[activeInputId] = (newValues[activeInputId] || '') + letter;
      setValues(newValues);
      localStorage.setItem(storageKey, JSON.stringify(newValues));
      
      // Clear incorrect status when typing
      if (incorrectInputs.has(activeInputId)) {
        const newIncorrectInputs = new Set(incorrectInputs);
        newIncorrectInputs.delete(activeInputId);
        setIncorrectInputs(newIncorrectInputs);
      }
      
      const isAllCorrect = inputs.every(input => 
        normalizeText(newValues[input.inputId] || '') === normalizeText(input.correctAnswer || '')
      );

      if (isAllCorrect && !isCompleted) {
        // Store values before setting completion
        localStorage.setItem(storageKey, JSON.stringify(newValues));
        setIsCompleted(true);
        setShowKeyboard(false);
        onComplete();
        console.log(`${ASSETS_URL}/well-done.mp3`);
        const audio = new Audio(`${ASSETS_URL}/well-done.mp3`);
        audio.play().catch(console.error);
      }
    }
  };

  const handleBackspace = () => {
    if (activeInputId && !isCompleted) {
      const newValues = { ...values };
      newValues[activeInputId] = newValues[activeInputId]?.slice(0, -1) || '';
      setValues(newValues);
      
      // Clear incorrect status when deleting
      if (incorrectInputs.has(activeInputId)) {
        const newIncorrectInputs = new Set(incorrectInputs);
        newIncorrectInputs.delete(activeInputId);
        setIncorrectInputs(newIncorrectInputs);
      }
    }
  };

  // NEW: Handle blur validation
  const handleInputBlur = (input) => {
    if (isCompleted) return;
    
    const currentValue = values[input.inputId] || '';
    const isCorrect = normalizeText(currentValue) === normalizeText(input.correctAnswer || '');
    
    if (!isCorrect && currentValue.trim() !== '') {
      // Mark as incorrect if value is wrong and not empty
      const newIncorrectInputs = new Set(incorrectInputs);
      newIncorrectInputs.add(input.inputId);
      setIncorrectInputs(newIncorrectInputs);
    } else if (isCorrect && incorrectInputs.has(input.inputId)) {
      // Remove from incorrect if now correct
      const newIncorrectInputs = new Set(incorrectInputs);
      newIncorrectInputs.delete(input.inputId);
      setIncorrectInputs(newIncorrectInputs);
    }
  };

  const handleInputChange = (input, value) => {
    if (!isCompleted) {
      const newValues = { ...values };
      newValues[input.inputId] = value;
      setValues(newValues);
      localStorage.setItem(storageKey, JSON.stringify(newValues));
      
      // Clear incorrect status when typing
      if (incorrectInputs.has(input.inputId)) {
        const newIncorrectInputs = new Set(incorrectInputs);
        newIncorrectInputs.delete(input.inputId);
        setIncorrectInputs(newIncorrectInputs);
      }
      
      const isAllCorrect = inputs.every(input => 
        normalizeText(newValues[input.inputId] || '') === normalizeText(input.correctAnswer || '')
      );

      if (isAllCorrect && !isCompleted) {
        localStorage.setItem(storageKey, JSON.stringify(newValues));
        setIsCompleted(true);
        setShowKeyboard(false);
        onComplete();
        
        try {
          const audio = new Audio(`${ASSETS_URL}/well-done.mp3`);
          audio.play().catch(() => {
            console.log('Audio not available');
          });
        } catch (error) {
          console.log('Audio playback error');
        }
      }
    }
  };

  return (
    <div className="relative">
      {/* Container with word-list design from HTML */}
      <div className="max-w-full mx-auto bg-slate-50 p-4 rounded-xl border border-gray-200">
        <div className={getResponsiveGridClass(itemsPerRow)}>
          {inputs.map(input => {
            const isIncorrect = incorrectInputs.has(input.inputId);
            const currentValue = values[input.inputId] || '';
            
            return (
              <div key={input.inputId} className="bg-white p-4 rounded-lg border-l-4 border-l-blue-400 transition-all duration-300 hover:translate-x-1 hover:shadow-xl" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }} onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 16px rgba(96, 165, 250, 0.2)'} onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)'}>
                {inlineInputs ? (
                  // Inline layout with all three elements
                  <div className="flex items-center gap-4">
                    {/* Image container with fixed width */}
                    {input.imageUrl && (
                      <div className="flex-shrink-0 w-20">
                        <img 
                          src={`${ASSETS_URL}${input.imageUrl}`} 
                          alt="" 
                          className="w-20 h-20 object-cover rounded"
                        />
                      </div>
                    )}
                    
                    {/* Hint text with fixed width */}
                    {input.hintText && (
                      <div className="flex-shrink-0 max-w-[8rem] min-w-fit">
                        <p className="text-sm text-gray-600 whitespace-normal">{input.hintText}</p>
                      </div>
                    )}
                    
                    {/* Input field takes remaining space */}
                    <div className="flex-1 min-w-[120px]">
                      <input
                        type="text"
                        value={currentValue}
                        placeholder="შეიყვანეთ სიტყვა..."
                        onChange={(e) => handleInputChange(input, e.target.value)}
                        onBlur={() => handleInputBlur(input)}
                        onFocus={() => {
                          if (!isCompleted) {
                            setActiveInputId(input.inputId);                  
                            setShowKeyboard(true);
                            // Clear incorrect status when focusing back
                            if (incorrectInputs.has(input.inputId)) {
                              const newIncorrectInputs = new Set(incorrectInputs);
                              newIncorrectInputs.delete(input.inputId);
                              setIncorrectInputs(newIncorrectInputs);
                            }
                            // Auto-scroll to input
                            setTimeout(() => {
                              const activeElement = document.activeElement;
                              if (activeElement) {
                                activeElement.scrollIntoView({ 
                                  behavior: 'smooth', 
                                  block: 'center' 
                                });
                              }
                            }, 100);
                          }
                        }}
                        className={`w-full p-2 border-2 rounded-lg text-base transition-all duration-200 bg-gray-50 text-gray-800 hover:border-blue-400 hover:bg-white focus:outline-none focus:border-blue-500 focus:bg-white placeholder:text-gray-400 placeholder:italic
                          ${isCompleted ? 'bg-green-50 border-green-500' : 
                            isIncorrect ? 'bg-red-50 border-red-500 text-red-700' : 'border-gray-200'}
                        `}
                        style={{
                          boxShadow: isCompleted ? undefined : (activeInputId === input.inputId ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : undefined)
                        }}
                      />
                      
                      {/* NEW: Show correct answer when input is incorrect */}
                      {isIncorrect && !isCompleted && (
                        <div className="mt-1 text-sm text-green-600 font-medium">
                          სწორი პასუხი: {input.correctAnswer}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Vertical layout (original)
                  <>
                    <div className="flex items-center gap-3">
                      {input.imageUrl && (
                        <img 
                          src={`${ASSETS_URL}${input.imageUrl}`} 
                          alt="" 
                          className="w-20 h-20 object-cover rounded"
                        />
                      )}
                      {input.hintText && (
                        <p className="text-sm text-gray-600 truncate">{input.hintText}</p>
                      )}
                    </div>
                    <input
                      type="text"
                      value={currentValue}
                      placeholder="შეიყვანეთ სიტყვა..."
                      onChange={(e) => handleInputChange(input, e.target.value)}
                      onBlur={() => handleInputBlur(input)}
                      onPaste={(e) => {
                        e.preventDefault();
                      }}
                      onFocus={() => {
                        if (!isCompleted) {
                          setShowKeyboard(true);
                          setActiveInputId(input.inputId);
                          // Clear incorrect status when focusing back
                          if (incorrectInputs.has(input.inputId)) {
                            const newIncorrectInputs = new Set(incorrectInputs);
                            newIncorrectInputs.delete(input.inputId);
                            setIncorrectInputs(newIncorrectInputs);
                          }
                        }
                      }}
                      className={`w-full p-2 border-2 rounded-lg text-base transition-all duration-200 bg-gray-50 text-gray-800 hover:border-blue-400 hover:bg-white focus:outline-none focus:border-blue-500 focus:bg-white placeholder:text-gray-400 placeholder:italic
                        ${isCompleted ? 'bg-green-50 border-green-500' : 
                          isIncorrect ? 'bg-red-50 border-red-500 text-red-700' : 'border-gray-200'}
                      `}
                      style={{
                        boxShadow: isCompleted ? undefined : (activeInputId === input.inputId ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : undefined)
                      }}
                    />
                    
                    {/* NEW: Show correct answer when input is incorrect */}
                    {isIncorrect && !isCompleted && (
                      <div className="mt-2 text-sm text-green-600 font-medium">
                        სწორი პასუხი: {input.correctAnswer}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showKeyboard && !isCompleted && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg p-2 z-50">
          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-12 gap-1 mb-2">
            <button
  onMouseDown={(e) => {
    e.preventDefault();
    setCapsLock(!capsLock);
  }}
  className={`p-1 border rounded col-span-2 text-sm ${capsLock ? 'bg-blue-100' : ''}`}
  tabIndex={-1}
>
                Caps Lock
              </button>
              {abkhazLetters.map((letter, index) => (
                <button
                key={index}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleKeyPress(capsLock ? letter.upper : letter.lower);
                }}
                className="p-1 border rounded hover:bg-gray-100 text-center text-sm h-8"
                disabled={isCompleted}
                tabIndex={-1}
              >
                 {letter.lower === ' ' ? 'Space' : (capsLock ? letter.upper : letter.lower)}
                </button>
              ))}
            </div>
            <div className="flex justify-center gap-2">
            <button
  onMouseDown={(e) => {
    e.preventDefault();
    handleBackspace();
  }}
  disabled={isCompleted}
  className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
  tabIndex={-1}
>
                Backspace
              </button>
              <button
  onMouseDown={(e) => {
    e.preventDefault();
    console.log('Closing keyboard, current values:', values);
    setShowKeyboard(false);
  }}
  className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
  tabIndex={-1}
>
                Close Keyboard
              </button>
            </div>
          </div>
        </div>
      )}
      
      {isCompleted && <SuccessOverlay />}
    </div>
  );
};

   // ExerciseContent Component
    const ExerciseContent = ({ exerciseType, exerciseData, onComplete }) => {
      console.log('ExerciseContent render - exerciseType:', exerciseType);
      const { 
        selectionOptions = [], 
        textInputs = [], 
        connections = [],
        itemsPerRow,
        inlineTextInputs, // Get the new property from the card data 
      } = exerciseData;
      const isCompleted = isCurrentExerciseCompleted(currentCardIndex);

      return (
        <div className="relative">
          {exerciseType === 'selection' && (
            <SelectableItems 
              items={selectionOptions}
              itemsPerRow={itemsPerRow}
              onComplete={() => onComplete()}
              isCompleted={isCompleted}
            />
          )}
          
          {exerciseType === 'text_input' && (
            <TextInputs 
              inputs={textInputs}
              itemsPerRow={itemsPerRow}
              onComplete={() => onComplete()}
              isCompleted={isCompleted}
              inlineInputs={inlineTextInputs}
              
            />
          )}
          
          {exerciseType === 'connection' && (
            <Connections 
              items={connections}
              onComplete={() => onComplete()}
              isCompleted={isCompleted}
              cardIndex={currentCardIndex}  // Add this prop
            />
          )}
        </div>
      );
    };

  return (
    <div className="min-h-screen bg-gray-50 font-['HelveticaRegular']">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center sm:justify-between items-center py-4">
            <div className="flex items-center gap-2 sm:ml-12">
              <BookOpen className="text-blue-600 flex-shrink-0" size={24} />
              <h1 className="hidden sm:block text-xl font-bold text-gray-900 font-['bpg-caps'] whitespace-nowrap">
                აფხაზური ენის კურსი
              </h1>
            </div>
            <div className="absolute right-4 sm:relative sm:right-auto flex items-center gap-2">
              {/* Dictionary Button */}
              <button
                onClick={() => setShowDictionary(true)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Open dictionary"
                title="ლექსიკონი"
              >
                <img 
                  src={`${ASSETS_URL}/assets/images/dic_icon.png`}
                  alt="ლექსიკონი"
                  className="w-6 h-6"
                />
              </button>
              {/* Home Button */}
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors flex-shrink-0"
              >
                <ArrowLeft size={16} />
                <span className="font-['bpg-caps'] text-sm sm:text-base">მთავარი</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Dictionary Modal */}
      <Dictionary 
        isOpen={showDictionary} 
        onClose={() => setShowDictionary(false)} 
      />

      {/* Navigation Menu */}
      <NavigationMenu 
        currentCardId={currentCard?.cardId}
        onNavigate={handleNavigateToCard}
        isOpen={showNavigation}
        onToggle={handleNavigationToggle}
      />
    {/* Fixed Mini Audio Player */}
    {showMiniPlayer && currentCard?.audioUrl && (
        <div className="fixed bottom-4 left-4 right-4 z-40 bg-white rounded-lg shadow-xl border border-gray-200 p-3">
          <MiniAudioPlayer 
            url={`${ASSETS_URL}${currentCard.audioUrl}`} 
            title={currentCard.title}
          />
        </div>
      )}
      <div className={`max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6 m-8 transition-all duration-300 ${showKeyboard ? 'pb-80' : showMiniPlayer ? 'mb-20' : ''}`}>
        {currentCard && (
          <div className="space-y-6">
            <div className="flex justify-between items-start">
              <h2 className="text-large font-bold font-['bpg-caps']">{currentCard.title}</h2>
              {currentCard.lessonName && (
                <div className="text-right">
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-['bpg-caps']">
                    {currentCard.lessonName}
                  </span>
                </div>
              )}
            </div>
            
            {/*currentCard.audioUrl && (
              <div className="my-4" >
                <AudioPlayer url={`${ASSETS_URL}${currentCard.audioUrl}`} />
              </div>
            )*/}

            <HTMLWithAudioRenderer 
              htmlContent={currentCard.content} 
              className="prose max-w-none"
            />

            {currentCard.cardType === 'exercise' && hasValidExerciseData(currentCard) && (
              console.log('About to render ExerciseContent with:', {
                key: `exercise-${currentCardIndex}`,
                exerciseType: currentCard.exerciseType,
                cardType: currentCard.cardType,
                itemsPerRow: currentCard.itemsPerRow || 2,
              }) || 
              <ExerciseContent 
                key={`exercise-${currentCardIndex}`}
                exerciseType={currentCard.exerciseType}
                exerciseData={{
                  selectionOptions: currentCard.selectionOptions,
                  textInputs: currentCard.textInputs,
                  connections: currentCard.connections,
                  itemsPerRow: currentCard.itemsPerRow || 2,
                  inlineTextInputs: currentCard.inlineTextInputs 
                }}
                onComplete={() => handleExerciseComplete(currentCardIndex)}
              />
            )}

            <div className="flex justify-between items-center mt-6">
              <button
                onClick={handlePrevious}
                disabled={currentCardIndex === 0}
                className="flex items-center px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50 font-['bpg-caps']"
              >
                <ChevronLeft className="mr-2" /> წინა
              </button>
              
              <button
                onClick={handleNext}
                disabled={currentCardIndex === cards.length - 1}
                className="flex items-center px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50 font-['bpg-caps']"
              >
                შემდეგი <ChevronRight className="ml-2" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;